import { ProductFeaturesMap } from 'inquiry/lib/ProductFeatures';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

export enum ApiEndpoint {
  // Singleton endpoints
  CurrentUser = 'current-user',
  CurrentInquirySession = 'current-inquiry-session',
  Organization = 'organization',
  Organizations = 'organizations',

  // Resource endpoints
  Account = 'accounts',
  AccountAction = 'account-actions',
  AccountStatus = 'account-statuses',
  AccountType = 'account-types',
  AccountTypeGridLayout = 'account-type-grid-layouts',
  AccountVariableDefinition = 'account-variable-definitions',
  AnalyticsQueryTemplates = 'analytics-query-templates',
  ApiKey = 'api-keys',
  ApiLog = 'api-logs',
  Blueprint = 'blueprints',
  Case = 'cases',
  CaseAssignmentPolicy = 'case-assignment-policies',
  CaseComment = 'case-comments',
  CaseMessage = 'case-messages',
  CaseQueue = 'case-queues',
  CaseQuery = 'case-queries',
  CaseQueryResult = 'case-query-results',
  CaseTemplate = 'case-templates',
  CaseVariableDefinition = 'case-variable-definitions',
  ComplianceRule = 'compliance-rules',
  Device = 'devices',
  Environment = 'environments',
  Event = 'events',
  Exporter = 'exporters',
  ExternalIntegration = 'external-integrations',
  ExternalIntegrationCall = 'external-integration-calls',
  ExternalIntegrationConfig = 'external-integration-configs',
  ExternalIntegrationDataSource = 'external-integration/data-sources',
  ExternalIntegrationLog = 'external-integration-logs',
  ExternalIntegrationVendor = 'external-integration-vendors',
  File = 'files',
  Graph = 'graphs',
  GraphDatumType = 'graph-datum-types',
  GraphNode = 'graph-nodes',
  GraphQuery = 'graph-queries',
  GraphQueryTemplate = 'graph-query-templates',
  GraphSnapshot = 'graph-snapshots',
  ImporterPipeline = 'importer-pipelines',
  ImporterRun = 'importer-pipeline-runs',
  Inquiry = 'inquiries',
  InquirySession = 'inquiry-sessions',
  InquiryTemplate = 'inquiry-templates',
  InquiryTemplateModule = 'inquiry-template-modules',
  InquiryTemplateVersion = 'inquiry-template-versions',
  InsightQuery = 'insight-queries',
  Invoice = 'invoices',
  List = 'lists',
  ListItem = 'list-items',
  ListMatch = 'list-match',
  OrganizationsSSO = 'organizations/sso',
  OnboardingFlowV1 = 'onboarding-flow-v1s',
  OrganizationPlan = 'organization-plans',
  OrganizationPlanMigration = 'organization-plan-migrations',
  PasswordReset = 'password-resets',
  Persona = 'personas',
  PlatformNode = 'platform-nodes',
  ReviewQueue = 'review-queues',
  ReviewQueueTask = 'review-queue-tasks',
  Role = 'roles',
  SAR = 'sars',
  SARConfiguration = 'sar-configurations',
  Selfie = 'selfie',
  SimulatePreset = 'simulate-presets',
  TableView = 'table-views',
  Tag = 'tags',
  Team = 'teams',
  Template = 'templates',
  Theme = 'themes',
  ThemeSet = 'theme-sets',
  Transaction = 'transactions',
  TransactionLabel = 'transaction-labels',
  TransactionStatus = 'transaction-statuses',
  TransactionType = 'transaction-types',
  TransactionTypeFilters = 'transaction-types/basics',
  TransactionTypeGridLayout = 'transaction-type-grid-layouts',
  TransactionVariableDefinition = 'transaction-variable-definitions',
  Translations = 'translations',
  Trigger = 'triggers',
  FeatureFlag = 'feature-flags',
  User = 'users',
  UserAuditLog = 'user-audit-logs',
  UserAuthentication = 'user-authentications',
  UserNotification = 'user-notifications',
  UserSession = 'user-sessions',
  Verification = 'verifications',
  VerificationTemplate = 'verification-templates',
  VerificationTemplateVersion = 'verification-template-versions',
  Webhook = 'webhooks',
  WebhookEvent = 'webhook-events',
  Workflow = 'workflows',
  WorkflowBlock = 'workflow-blocks',
  WorkflowDeployment = 'workflow-deployments',
  WorkflowModule = 'workflow-modules',
  WorkflowRun = 'workflow-runs',
  WorkflowRunStep = 'workflow-steps',
  WorkflowSchemaFullOld = 'workflows/schema',
  WorkflowSchema = 'workflow-schemas',
  WorkflowSchemaCriteriaVariableSchemas = 'workflow-schemas/criteria-variable-schemas',
  WorkflowSchemaOperators = 'workflow-schemas/operators',
  WorkflowSchemaRedactableFieldConfigs = 'workflow-schemas/redactable-field-configs',
  WorkflowSchemaFilterFieldConfigs = 'workflow-schemas/filter-field-configs',
  WorkflowSchemaWorkflowActions = 'workflow-schemas/workflow-actions',
  WorkflowSchemaJsonSchema = 'workflow-schemas/json-schema',
  WorkflowSchemaPrimitives = 'workflow-schemas/primitives',
  WorkflowVersion = 'workflow-versions',

  // List endpoints with multiple types also have specific create endpoints
  ExporterAccount = 'exporter/accounts',
  ExporterCase = 'exporter/cases',
  ExporterCustomFiles = 'exporter/custom-files',
  ExporterDataset = 'exporter/dataset',
  ExporterMetricsVerificationVolume = 'exporter/metrics/verifications/volume',
  ExporterMetricsVerificationChecks = 'exporter/metrics/verifications/checks',
  ExporterGraph = 'exporter/graphs',
  ExporterInquiry = 'exporter/inquiries',
  ExporterInsightQueries = 'exporter/insight-queries',
  ExporterMetricsInquiry = 'exporter/metrics/inquiry',
  ExporterReport = 'exporter/reports',
  ExporterReviewQueueTask = 'exporter/review-queue-tasks',
  ExporterTransactions = 'exporter/txns',

  Importer = 'importers',
  ImporterReportAddressLookup = 'importer/report/address-lookups',
  ImporterReportAdverseMedia = 'importer/report/adverse-media',
  ImporterReportBusinessAdverseMedia = 'importer/report/business-adverse-media',
  ImporterReportBusinessLookup = 'importer/report/business-lookups',
  ImporterReportBusinessWatchlist = 'importer/report/business-watchlists',
  ImporterReportEmailAddress = 'importer/report/email-addresses',
  ImporterReportPhoneNumber = 'importer/report/phone-numbers',
  ImporterReportPoliticallyExposedPerson = 'importer/report/politically-exposed-people',
  ImporterReportProfileNonAuthoritative = 'importer/report/profile-non-authoritatives',
  ImporterReportSocialMedia = 'importer/report/social-media',
  ImporterReportWatchlist = 'importer/report/watchlists',

  SelfieCenterOnly = 'selfie/center-onlies',
  SelfieProfileAndCenter = 'selfie/profile-and-centers',

  VerificationAamva = 'verification/aamvas',
  VerificationBusinessWebsite = 'verification/business-websites',
  VerificationCertificateKorea = 'verification/certificate-koreas',
  VerificationDatabase = 'verification/databases',
  VerificationDatabaseAadhaar = 'verification/database-aadhaars',
  VerificationDatabaseBusiness = 'verification/database-businesses',
  VerificationDatabaseBusinessFootprint = 'verification/database-business-footprints',
  VerificationDatabaseEcbsv = 'verification/database-ecbsvs',
  VerificationDatabasePhoneCarrier = 'verification/database-phone-carriers',
  VerificationDatabaseSerpro = 'verification/database-serpros',
  VerificationDatabaseStandard = 'verification/database-standards',
  VerificationDatabaseTin = 'verification/database-tins',
  VerificationDatabaseVatNumber = 'verification/database-vat-numbers',
  VerificationDigitalIdFinnishTrustNetwork = 'verification/digital-id-finnish-trust-networks',
  VerificationDigitalIdOneId = 'verification/digital-id-one-id',
  VerificationDigitalIdSerpro = 'verification/digital-id-serpros',
  VerificationDigitalIdSingpass = 'verification/digital-id-singpasses',
  VerificationDigitalIdUkSharecode = 'verification/digital-id-uk-sharecodes',
  VerificationDocument = 'verification/documents',
  VerificationEmailAddress = 'verification/email-addresses',
  VerificationGovernmentId = 'verification/government-ids',
  VerificationGovernmentIdNfc = 'verification/government-id-nfcs',
  VerificationMobileDriversLicense = 'verification/mobile-drivers-license',
  VerificationPhoneNumber = 'verification/phone-numbers',
  VerificationSelfie = 'verification/selfies',

  Report = 'reports',
  ReportAddressLookup = 'report/address-lookups',
  ReportAdverseMedia = 'report/adverse-media',
  ReportBusinessAdverseMedia = 'report/business-adverse-media',
  ReportBusinessAssociatedPersons = 'report/business-associated-persons',
  ReportBusinessClassification = 'report/business-classifications',
  ReportBusinessIndustryClassification = 'report/business-industry-classifications',
  ReportBusinessLiensLookup = 'report/business-liens-lookup',
  ReportBusinessLookup = 'report/business-lookups',
  ReportBusinessNonprofit = 'report/business-nonprofits',
  ReportBusinessOnlinePresence = 'report/business-online-presences',
  ReportBusinessRegistrationsLookup = 'report/business-registrations-lookup',
  ReportBusinessSocialMedia = 'report/business-social-media',
  ReportBusinessWatchlist = 'report/business-watchlists',
  ReportBusinessWebsite = 'report/business-websites',
  ReportChainalysisAddressScreening = 'report/integration/chainalysis-address-screening',
  ReportClearbitBusinessLookup = 'report/integration/clearbit-business-lookup',
  ReportCoinbaseCheckCryptoRisk = 'report/integration/coinbase-check-crypto-risk',
  ReportCryptoAddressWatchlist = 'report/crypto-address-watchlists',
  ReportCustomList = 'report/custom-lists',
  ReportEmailAddress = 'report/email-addresses',
  ReportEquifaxOneview = 'report/integration/equifax-oneview',
  ReportFinraBrokerCheck = 'report/finra-broker-checks',
  ReportKyckrBusinessLookup = 'report/integration/kyckr-business-lookup',
  ReportMiddeskBusinessLookup = 'report/integration/middesk-business-lookup',
  ReportMxAccount = 'report/integration/mx-account',
  ReportPhoneNumber = 'report/phone-numbers',
  ReportPoliticallyExposedPerson = 'report/politically-exposed-people',
  ReportProfile = 'report/profiles',
  ReportProfileNonAuthoritative = 'report/profile-non-authoritatives',
  ReportScreening = 'report/screenings',
  ReportSecActionLookup = 'report/sec-action-lookups',
  ReportSentilinkApplicationRisk = 'report/integration/sentilink-application-risk',
  ReportSentilinkScores = 'report/integration/sentilink-scores',
  ReportSocialMedia = 'report/social-media',
  ReportSynthetic = 'report/synthetics',
  ReportTrmWalletScreening = 'report/integration/trm-wallet-screening',
  ReportWatchlist = 'report/watchlists',

  ReportRunsCustomList = 'report-run/custom-lists',

  ReportSnapshot = 'report-snapshot',

  ReportTemplate = 'report-templates',
  ReportTemplateAddressLookup = 'report-template/address-lookups',
  ReportTemplateAdverseMedia = 'report-template/adverse-media',
  ReportTemplateBusinessAdverseMedia = 'report-template/business-adverse-media',
  ReportTemplateBusinessAssociatedPersons = 'report-template/business-associated-persons',
  ReportTemplateBusinessClassification = 'report-template/business-classifications',
  ReportTemplateBusinessIndustryClassification = 'report-template/business-industry-classifications',
  ReportTemplateBusinessLookup = 'report-template/business-lookups',
  ReportTemplateBusinessLiensLookup = 'report-template/business-liens-lookup',
  ReportTemplateBusinessNonprofit = 'report-template/business-nonprofits',
  ReportTemplateBusinessOnlinePresence = 'report-template/business-online-presences',
  ReportTemplateBusinessRegistrationsLookup = 'report-template/business-registrations-lookups',
  ReportTemplateBusinessSocialMedia = 'report-template/business-social-media',
  ReportTemplateBusinessWatchlist = 'report-template/business-watchlists',
  ReportTemplateBusinessWebsite = 'report-template/business-websites',
  ReportTemplateCreateable = 'report-templates/createable',
  ReportTemplateChainalysisAddressScreening = 'report-template/integration/chainalysis-address-screenings',
  ReportTemplateClearbitBusinessLookup = 'report-template/integration/clearbit-business-lookup',
  ReportTemplateCoinbaseCheckCryptoRisk = 'report-template/integration/coinbase-check-crypto-risk',
  ReportTemplateCryptoAddressWatchlist = 'report-template/crypto-address-watchlists',
  ReportTemplateCustomList = 'report-template/custom-lists',
  ReportTemplateEmailAddress = 'report-template/email-addresses',
  ReportTemplateEquifaxOneview = 'report-template/integration/equifax-oneview',
  ReportTemplateFilters = 'report-templates/basics',
  ReportTemplateFinraBrokerCheck = 'report-template/finra-broker-checks',
  ReportTemplateKyckrBusinessLookup = 'report-template/integration/kyckr-business-lookups',
  ReportTemplateNovaCreditCashAtlas = 'report-template/integration/nova-credit-cash-atlas',
  ReportTemplateMiddeskBusinessLookup = 'report-template/integration/middesk-business-lookups',
  ReportTemplateMxAccount = 'report-template/integration/mx-accounts',
  ReportTemplateNovaCreditCreditPassport = 'report-template/integration/nova-credit-credit-passport',
  ReportTemplatePhoneNumber = 'report-template/phone-numbers',
  ReportTemplatePoliticallyExposedPerson = 'report-template/politically-exposed-people',
  ReportTemplateProfile = 'report-template/profiles',
  ReportTemplateProfileNonAuthoritative = 'report-template/profile-non-authoritatives',
  ReportTemplateScreening = 'report-template/screening',
  ReportTemplateSecActionLookup = 'report-template/sec-action-lookups',
  ReportTemplateSentilinkApplicationRisk = 'report-template/integration/sentilink-application-risk',
  ReportTemplateSentilinkScores = 'report-template/integration/sentilink-scores',
  ReportTemplateSocialMedia = 'report-template/social-media',
  ReportTemplateSynthetic = 'report-template/synthetics',
  ReportTemplateTrmWalletScreening = 'report-template/integration/trm-wallet-screening',
  ReportTemplateWatchlist = 'report-template/watchlists',
  ReportTemplateVersion = 'report-template-versions',
  ReportTemplateVersionCustomList = 'report-template-version/custom-lists',

  // Api Metrics
  MetricsApiLogsPaths = 'metrics/api-logs/paths',
  MetricsApiLogsVolumeOverTime = 'metrics/api-logs/volume-over-time',

  // Cases Metrics
  MetricsCases = 'metrics/cases',
  MetricsCasesStatusOverTime = 'metrics/cases/status-over-time',

  // Inquiries Metrics
  MetricsInquiries = 'metrics/inquiries',
  MetricsInquiriesFunnelConversionOverTime = 'metrics/inquiries/conversion-over-time',
  MetricsInquiriesFunnelFlow = 'metrics/inquiries/funnel-flow',
  MetricsInquiriesMedianCompletionVolume = 'metrics/inquiries/median-completion',
  MetricsInquiriesRecentActivity = 'metrics/inquiries/recent-activity',
  MetricsInquiriesRiskDistribution = 'metrics/inquiries/risk-distribution',
  MetricsInquiriesVolume = 'metrics/inquiries/volume',
  MetricsInquiriesVolumeOverTime = 'metrics/inquiries/volume-over-time',

  // Transactions Metrics
  MetricsTransactions = 'metrics/txns',
  MetricsTransactionsVolumeOverTime = 'metrics/txns/volume-over-time',

  // Verifications Metrics
  MetricsVerifications = 'metrics/verifications',
  MetricsVerificationsFilteredVolume = 'metrics/verifications/volume-with-filters',
  MetricsVerificationsVolumeOverTime = 'metrics/verifications/volume-over-time',
  MetricsVerificationsFilteredChecksOverTime = 'metrics/verifications/checks-over-time-filters',

  // Reports Metrics
  MetricsReportsSummary = 'metrics/reports/reports-summary',
  MetricsReportsOverTime = 'metrics/reports/reports-over-time',

  // Workflow Metrics
  MetricsWorkflowRuns = 'metrics/workflow-runs',

  Document = 'documents',
  DocumentGeneric = 'document/generics',
  DocumentGovernmentId = 'document/government-ids',
  DocumentFile = 'document-files',

  DataSourceS3Config = 'data-source/s3/config',
  DataSourceS3ListObjects = 'data-source/s3/list-objects',

  // Growth
  Omnisearch = 'omnisearch/search',
  HelpCenterArticles = 'article_search',
  WorkrampInstantAuth = 'academy/workramp_instant_auth',

  TrackingEvents = 't',

  // Simulate
  SimulatePresetVerification = 'simulate/preset/verification',
}

export enum ApiModel {
  Account = 'account',
  AccountAction = 'account-action',
  AccountStatus = 'account-status',
  AccountType = 'account-type',
  AccountTypeGridLayout = 'account-type-grid-layout',
  AccountVariableDefinition = 'account-variable-definition',
  AnalyticsQueryTemplate = 'analytics-query-template',
  Blueprint = 'blueprint',
  Case = 'case',
  CaseAssignmentPolicy = 'case-assignment-policy',
  CaseComment = 'case-comment',
  CaseMessage = 'case-message',
  CaseQueue = 'case-queue',
  CaseQuery = 'case-query',
  CaseQueryResult = 'case-query-result',
  CaseTemplate = 'case-template',
  CaseVariableDefinition = 'case-variable-definition',
  ComplianceRule = 'compliance-rule',
  CurrentInquirySession = 'current-inquiry-session',
  CurrentUser = 'current-user',
  Device = 'device',
  Environment = 'environment',
  EnvironmentProduction = 'environment/production',
  EnvironmentSandbox = 'environment/sandbox',
  Event = 'event',
  Graph = 'graph',
  GraphDatumType = 'graph-datum-type',
  GraphNode = 'graph-node',
  GraphQuery = 'graph-query',
  GraphQueryTemplate = 'graph-query-template',
  GraphSnapshot = 'graph-snapshot',

  // Importer Pipelines
  ImporterPipelineCsv = 'importer-pipeline/csv-external-integration-importer-pipeline',
  ImporterPipelineMysql = 'importer-pipeline/mysql-external-integration-importer-pipeline',
  ImporterPipelineSnowflake = 'importer-pipeline/snowflake-external-integration-importer-pipeline',
  ImporterPipelineRedshift = 'importer-pipeline/redshift-external-integration-importer-pipeline',
  ImporterPipelineS3 = 'importer-pipeline/s3-external-integration-importer-pipeline',
  ImporterPipelineGoogleSheets = 'importer-pipeline/google-sheets-external-integration-importer-pipeline',

  ImporterRun = 'importer-pipeline-run',
  Inquiry = 'inquiry',
  InquirySession = 'inquiry-session',
  InquiryTemplate = 'inquiry-template',
  InquiryTemplateModule = 'inquiry-template-module',
  InquiryTemplateVersion = 'inquiry-template-version',
  InquiryTemplateVersionDiff = 'inquiry-template-version-diff',
  InsightQuery = 'insight-query',
  Invoice = 'invoice',
  OnboardingFlowV1 = 'onboarding-flow/v1',
  Organization = 'organization',
  OrganizationPlan = 'organization-plan',
  OrganizationPlanMigration = 'organization-plan-migration',
  PartialInquiryTemplateVersion = 'partial-inquiry-template-version',
  PasswordResetEmail = 'password-reset/email',
  PlatformNode = 'platform-node',
  Role = 'role',
  SAR = 'sar',
  SARConfiguration = 'sar-configurations',
  TableView = 'table-view',
  Team = 'team',
  Template = 'template',
  Theme = 'theme',
  ThemeSet = 'theme-set',
  Transaction = 'transaction',
  TransactionLabel = 'transaction-label',
  TransactionStatus = 'transaction-status',
  TransactionType = 'transaction-type',
  TransactionTypeGridLayout = 'transaction-type-grid-layout',
  TransactionVariableDefinition = 'transaction-variable-definition',
  TriggerAccount = 'trigger-account',
  TriggerEvent = 'trigger-event',
  TriggerSchedule = 'trigger-schedule',
  TriggerRecurring = 'trigger-recurring',
  TriggerApi = 'trigger-api',
  TriggerCase = 'trigger-case',
  TriggerDirect = 'trigger-direct',
  TriggerComputedField = 'trigger-computed-field',
  User = 'user',
  UserAuditLog = 'user-audit-log',
  UserAuthentication = 'user-authentication',
  UserNotification = 'user-notification',
  UserSession = 'user-session',
  Webhook = 'webhook',
  WebhookEvent = 'webhook-event',

  List = 'list',
  ListGovernmentIdNumber = 'list/government-id-number',
  ListIpAddress = 'list/ip-address',
  ListName = 'list/name',
  ListPhoneNumber = 'list/phone-number',
  ListEmailAddress = 'list/email-address',
  ListGeolocation = 'list/geolocation',
  ListBrowserFingerprint = 'list/browser-fingerprint',
  ListDeviceFingerprint = 'list/device-fingerprint',
  ListFace = 'list/face',
  ListCountry = 'list/country',
  ListField = 'list/field',
  ListString = 'list/string',
  ListSelfieBackground = 'list/selfie-background',

  ListItem = 'list-item',
  ListItemGovernmentIdNumber = 'list-item/government-id-number',
  ListItemIpAddress = 'list-item/ip-address',
  ListItemName = 'list-item/name',
  ListItemPhoneNumber = 'list-item/phone-number',
  ListItemEmailAddress = 'list-item/email-address',
  ListItemGeolocation = 'list-item/geolocation',
  ListItemBrowserFingerprint = 'list-item/browser-fingerprint',
  ListItemDeviceFingerprint = 'list-item/device-fingerprint',
  ListItemFace = 'list-item/face',
  ListItemCountry = 'list-item/country',
  ListItemField = 'list-item/field',
  ListItemString = 'list-item/string',
  ListItemSelfieBackground = 'list-item/selfie-background',

  ListMatch = 'list-match',

  ReviewQueue = 'review-queue',
  ReviewQueueTask = 'review-queue-task',

  Workflow = 'workflow',
  WorkflowBlock = 'workflow-block',
  WorkflowDeployment = 'workflow-deployment',
  WorkflowModule = 'workflow-module',
  WorkflowRun = 'workflow-run',
  WorkflowRunSimulated = 'workflow-run/simulated',
  WorkflowVersion = 'workflow-version',
  // N.B. This is not the same as stepsConfig workflow steps
  WorkflowRunStep = 'workflow-step',
  WorkflowRunStepAction = 'workflow-step/action',
  WorkflowRunStepConditional = 'workflow-step/conditional',
  WorkflowRunStepLoop = 'workflow-step/loop',
  WorkflowRunStepModule = 'workflow-step/module',
  WorkflowRunStepParallel = 'workflow-step/parallel',
  WorkflowRunStepWait = 'workflow-step/wait',
  WorkflowRunVariablePersonaObject = 'workflow-run/variable-store/persona-object-variable',
  WorkflowRunVariablePlatformIntegration = 'workflow-run/variable-store/platform-integration-variable',
  WorkflowRunVariableOutput = 'workflow-run/variable-store/output-variable',
  WorkflowRunVariableTriggerParams = 'workflow-run/variable-store/trigger-params-variable',
  WorkflowRunVariableLoop = 'workflow-run/variable-store/loop-variable',

  Report = 'report',
  ReportAddressLookup = 'report/address-lookup',
  ReportAdverseMedia = 'report/adverse-media',
  ReportBusinessAdverseMedia = 'report/business-adverse-media',
  ReportBusinessAssociatedPersons = 'report/business-associated-persons',
  ReportBusinessClassification = 'report/business-classification',
  ReportBusinessIndustryClassification = 'report/business-industry-classification',
  ReportBusinessLiensLookup = 'report/business-liens-lookup',
  ReportBusinessLookup = 'report/business-lookup',
  ReportBusinessNonprofit = 'report/business-nonprofit',
  ReportBusinessOnlinePresence = 'report/business-online-presence',
  ReportBusinessRegistrationsLookup = 'report/business-registrations-lookup',
  ReportBusinessSocialMedia = 'report/business-social-media',
  ReportBusinessWatchlist = 'report/business-watchlist',
  ReportBusinessWebsite = 'report/business-website',
  ReportCryptoAddressWatchlist = 'report/crypto-address-watchlist',
  ReportCustomList = 'report/custom-list',
  ReportEmailAddress = 'report/email-address',
  ReportFinraBrokerCheck = 'report/finra-broker-check',
  ReportPhoneNumber = 'report/phone-number',
  ReportPoliticallyExposedPerson = 'report/politically-exposed-person',
  ReportProfile = 'report/profile',
  ReportProfileNonAuthoritative = 'report/profile-non-authoritative',
  ReportScreening = 'report/screening',
  ReportSecActionLookup = 'report/sec-action-lookup',
  ReportSocialMedia = 'report/social-media',
  ReportSynthetic = 'report/synthetic',
  ReportWatchlist = 'report/watchlist',

  // Report Snapshots
  ReportSnapshotAdverseMedia = 'report-snapshot/adverse-media',
  ReportSnapshotBusinessNonprofit = 'report-snapshot/business-nonprofit',
  ReportSnapshotWatchlist = 'report-snapshot/watchlist',

  // integration reports
  ReportChainalysisAddressScreening = 'report/integration/chainalysis-address-screening',
  ReportClearbitBusinessLookup = 'report/integration/clearbit-business-lookup',
  ReportCoinbaseCheckCryptoRisk = 'report/integration/coinbase-check-crypto-risk',
  ReportEquifaxOneview = 'report/integration/equifax-oneview',
  ReportKyckrBusinessLookup = 'report/integration/kyckr-business-lookup',
  ReportMiddeskBusinessLookup = 'report/integration/middesk-business-lookup',
  ReportMxAccount = 'report/integration/mx-account',
  ReportSentilinkApplicationRisk = 'report/integration/sentilink-application-risk',
  ReportSentilinkScores = 'report/integration/sentilink-scores',
  ReportTrmWalletScreening = 'report/integration/trm-wallet-screening',

  ReportTemplate = 'report-template',
  ReportTemplateAddressLookup = 'report-template/address-lookup',
  ReportTemplateAdverseMedia = 'report-template/adverse-media',
  ReportTemplateBusinessAdverseMedia = 'report-template/business-adverse-media',
  ReportTemplateBusinessAssociatedPersons = 'report-template/business-associated-persons',
  ReportTemplateBusinessClassification = 'report-template/business-classification',
  ReportTemplateBusinessIndustryClassification = 'report-template/business-industry-classification',
  ReportTemplateBusinessLiensLookup = 'report-template/business-liens-lookup',
  ReportTemplateBusinessLookup = 'report-template/business-lookup',
  ReportTemplateBusinessNonprofit = 'report-template/business-nonprofit',
  ReportTemplateBusinessOnlinePresence = 'report-template/business-online-presence',
  ReportTemplateBusinessRegistrationsLookup = 'report-template/business-registrations-lookup',
  ReportTemplateBusinessSocialMedia = 'report-template/business-social-media',
  ReportTemplateBusinessWatchlist = 'report-template/business-watchlist',
  ReportTemplateBusinessWebsite = 'report-template/business-website',
  ReportTemplateCryptoAddressWatchlist = 'report-template/crypto-address-watchlist',
  ReportTemplateCustomList = 'report-template/custom-list',
  ReportTemplateEmailAddress = 'report-template/email-address',
  ReportTemplateFinraBrokerCheck = 'report-template/finra-broker-check',
  ReportTemplatePhoneNumber = 'report-template/phone-number',
  ReportTemplatePoliticallyExposedPerson = 'report-template/politically-exposed-person',
  ReportTemplateProfile = 'report-template/profile',
  ReportTemplateProfileNonAuthoritative = 'report-template/profile-non-authoritative',
  ReportTemplateScreening = 'report-template/screening',
  ReportTemplateSecActionLookup = 'report-template/sec-action-lookup',
  ReportTemplateSocialMedia = 'report-template/social-media',
  ReportTemplateSynthetic = 'report-template/synthetic',
  ReportTemplateWatchlist = 'report-template/watchlist',

  // integration report templates
  ReportTemplateClearbitBusinessLookup = 'report-template/integration/clearbit-business-lookup',
  ReportTemplateChainalysisAddressScreening = 'report-template/integration/chainalysis-address-screening',
  ReportTemplateCoinbaseCheckCryptoRisk = 'report-template/integration/coinbase-check-crypto-risk',
  ReportTemplateEquifaxOneview = 'report-template/integration/equifax-oneview',
  ReportTemplateKyckrBusinessLookup = 'report-template/integration/kyckr-business-lookup',
  ReportTemplateMiddeskBusinessLookup = 'report-template/integration/middesk-business-lookup',
  ReportTemplateMxAccount = 'report-template/integration/mx-account',
  ReportTemplateNovaCreditCashAtlas = 'report-template/integration/nova-credit-cash-atlas',
  ReportTemplateNovaCreditCreditPassport = 'report-template/integration/nova-credit-credit-passport',
  ReportTemplateSentilinkApplicationRisk = 'report-template/integration/sentilink-application-risk',
  ReportTemplateSentilinkScores = 'report-template/integration/sentilink-scores',
  ReportTemplateTrmWalletScreening = 'report-template/integration/trm-wallet-screening',

  ReportTemplateVersionAddressLookup = 'report-template-version/address-lookup',
  ReportTemplateVersionAdverseMedia = 'report-template-version/adverse-media',
  ReportTemplateVersionBusinessAdverseMedia = 'report-template-version/business-adverse-media',
  ReportTemplateVersionBusinessAssociatedPersons = 'report-template-version/business-associated-persons',
  ReportTemplateVersionBusinessClassification = 'report-template-version/business-classification',
  ReportTemplateVersionBusinessIndustryClassification = 'report-template-version/business-industry-classification',
  ReportTemplateVersionBusinessLiensLookup = 'report-template-version/business-liens-lookup',
  ReportTemplateVersionBusinessLookup = 'report-template-version/business-lookup',
  ReportTemplateVersionBusinessNonprofit = 'report-template-version/business-nonprofit',
  ReportTemplateVersionBusinessOnlinePresence = 'report-template-version/business-online-presence',
  ReportTemplateVersionBusinessRegistrationsLookup = 'report-template-version/business-registrations-lookup',
  ReportTemplateVersionBusinessSocialMedia = 'report-template-version/business-social-media',
  ReportTemplateVersionBusinessWatchlist = 'report-template-version/business-watchlist',
  ReportTemplateVersionBusinessWebsite = 'report-template-version/business-website',
  ReportTemplateVersionCryptoAddressWatchlist = 'report-template-version/crypto-address-watchlist',
  ReportTemplateVersionCustomList = 'report-template-version/custom-list',
  ReportTemplateVersionEmailAddress = 'report-template-version/email-address',
  ReportTemplateVersionFinraBrokerCheck = 'report-template-version/finra-broker-check',
  ReportTemplateVersionPhoneNumber = 'report-template-version/phone-number',
  ReportTemplateVersionPoliticallyExposedPerson = 'report-template-version/politically-exposed-person',
  ReportTemplateVersionProfile = 'report-template-version/profile',
  ReportTemplateVersionProfileNonAuthoritative = 'report-template-version/profile-non-authoritative',
  ReportTemplateVersionScreening = 'report-template-version/screening',
  ReportTemplateVersionSecActionLookup = 'report-template-version/sec-action-lookup',
  ReportTemplateVersionSocialMedia = 'report-template-version/social-media',
  ReportTemplateVersionSynthetic = 'report-template-version/synthetic',
  ReportTemplateVersionWatchlist = 'report-template-version/watchlist',

  // integration report template version
  ReportTemplateVersionChainalysisAddressScreening = 'report-template-version/integration/chainalysis-address-screening',
  ReportTemplateVersionClearbitBusinessLookup = 'report-template-version/integration/clearbit-business-lookup',
  ReportTemplateVersionEquifaxOneview = 'report-template-version/integration/equifax-oneview',
  ReportTemplateVersionKyckrBusinessLookup = 'report-template-version/integration/kyckr-business-lookup',
  ReportTemplateVersionMiddeskBusinessLookup = 'report-template-version/integration/middesk-business-lookup',
  ReportTemplateVersionMxAccount = 'report-template-version/integration/mx-account',
  ReportTemplateVersionSentilinkApplicationRisk = 'report-template-version/sentilink-application-risk',
  ReportTemplateVersionSentilinkScores = 'report-template-version/sentilink-scores',
  ReportTemplateVersionTrmWalletScreening = 'report-template-version/integration/trm-wallet-screening',

  ReportSnapshot = 'report-snapshot',
  ReportRun = 'report-run',
  ReportRunCustomList = 'report-run/custom-list',

  SelfieCenterOnly = 'selfie/center-only',
  SelfieProfileAndCenter = 'selfie/profile-and-center',

  Tag = 'tag',
  Translations = 'translations',

  Exporter = 'exporter',
  ExporterAccount = 'exporter/account',
  ExporterCase = 'exporter/case',
  ExporterCustomFile = 'exporter/custom-file',
  ExporterInsightQuery = 'exporter/insight-query',
  ExporterDataset = 'exporter/dataset',
  ExporterGraph = 'exporter/graph',
  ExporterInquiry = 'exporter/inquiry',
  ExporterMetricsVerificationVolume = 'exporter/metrics/verifications/volume',
  ExporterMetricsInquiry = 'exporter/metrics/inquiry',
  ExporterMetricsVerificationChecks = 'exporter/metrics/verifications/checks',
  ExporterReport = 'exporter/report',
  ExporterReviewQueueTask = 'exporter/review-queue-task',
  ExporterTransaction = 'exporter/txn',

  Importer = 'importer',
  ImporterReportAddressLookup = 'importer/report/address-lookup',
  ImporterReportAdverseMedia = 'importer/report/adverse-media',
  ImporterReportBusinessAdverseMedia = 'importer/report/business-adverse-media',
  ImporterReportBusinessLookup = 'importer/report/business-lookup',
  ImporterReportBusinessWatchlist = 'importer/report/business-watchlist',
  ImporterReportEmailAddress = 'importer/report/email-address',
  ImporterReportPhoneNumber = 'importer/report/phone-number',
  ImporterReportPoliticallyExposedPerson = 'importer/report/politically-exposed-person',
  ImporterReportProfileNonAuthoritative = 'importer/report/profile-non-authoritative',
  ImporterReportSocialMedia = 'importer/report/social-media',
  ImporterReportWatchlist = 'importer/report/watchlist',

  Verification = 'verification',
  // NB: Make sure each verification type is represented in the VerificationDetail component
  VerificationAamva = 'verification/aamva',
  VerificationBusinessWebsite = 'verification/business-website',
  VerificationCertificateKorea = 'verification/certificate-korea',
  VerificationDatabase = 'verification/database',
  VerificationDatabaseAadhaar = 'verification/database-aadhaar',
  VerificationDatabaseStandard = 'verification/database-standard',
  VerificationDatabaseBusiness = 'verification/database-business',
  VerificationDatabaseBusinessFootprint = 'verification/database-business-footprint',
  VerificationDatabaseEcbsv = 'verification/database-ecbsv',
  VerificationDatabasePhoneCarrier = 'verification/database-phone-carrier',
  VerificationDatabaseSerpro = 'verification/database-serpro',
  VerificationDatabaseTin = 'verification/database-tin',
  VerificationDatabaseVatNumber = 'verification/database-vat-number',
  VerificationDigitalIdFinnishTrustNetwork = 'verification/digital-id-finnish-trust-network',
  VerificationDigitalIdOneId = 'verification/digital-id-one-id',
  VerificationDigitalIdSerpro = 'verification/digital-id-serpro',
  VerificationDigitalIdSingpass = 'verification/digital-id-singpass',
  VerificationDigitalIdUkSharecode = 'verification/digital-id-uk-sharecode',
  VerificationDocument = 'verification/document',
  VerificationEmailAddress = 'verification/email-address',
  VerificationGovernmentId = 'verification/government-id',
  VerificationGovernmentIdNfc = 'verification/government-id-nfc',
  VerificationMobileDriversLicense = 'verification/mobile-drivers-license',
  VerificationPhoneNumber = 'verification/phone-number',
  VerificationSelfie = 'verification/selfie',

  VerificationTemplate = 'verification-template',
  VerificationTemplateAamva = 'verification-template/aamva',
  VerificationTemplateBusinessWebsite = 'verification-template/business-website',
  VerificationTemplateCertificateKorea = 'verification-template/certificate-korea',
  VerificationTemplateDatabase = 'verification-template/database',
  VerificationTemplateDatabaseAadhaar = 'verification-template/database-aadhaar',
  VerificationTemplateDatabaseStandard = 'verification-template/database-standard',
  VerificationTemplateDatabaseBusiness = 'verification-template/database-business',
  VerificationTemplateDatabaseBusinessFootprint = 'verification-template/database-business-footprint',
  VerificationTemplateDatabaseEcbsv = 'verification-template/database-ecbsv',
  VerificationTemplateDatabasePhoneCarrier = 'verification-template/database-phone-carrier',
  VerificationTemplateDatabaseSerpro = 'verification-template/database-serpro',
  VerificationTemplateDatabaseTin = 'verification-template/database-tin',
  VerificationTemplateDatabaseVatNumber = 'verification-template/database-vat-number',
  VerificationTemplateDigitalIdFinnishTrustNetwork = 'verification-template/digital-id-finnish-trust-network',
  VerificationTemplateDigitalIdOneId = 'verification-template/digital-id-one-id',
  VerificationTemplateDigitalIdSerpro = 'verification-template/digital-id-serpro',
  VerificationTemplateDigitalIdSingpass = 'verification-template/digital-id-singpass',
  VerificationTemplateDigitalIdUkSharecode = 'verification-template/digital-id-uk-sharecode',
  VerificationTemplateDocument = 'verification-template/document',
  VerificationTemplateEmailAddress = 'verification-template/email-address',
  VerificationTemplateGovernmentId = 'verification-template/government-id',
  VerificationTemplateGovernmentIdNfc = 'verification-template/government-id-nfc',
  VerificationTemplateMobileDriversLicense = 'verification-template/mobile-drivers-license',
  VerificationTemplatePhoneNumber = 'verification-template/phone-number',
  VerificationTemplateSelfie = 'verification-template/selfie',

  VerificationTemplateVersion = 'verification-template-version',
  VerificationTemplateVersionAamva = 'verification-template-version/aamva',
  VerificationTemplateVersionBusinessWebsite = 'verification-template-version/business-website',
  VerificationTemplateVersionCertificateKorea = 'verification-template-version/certificate-korea',
  VerificationTemplateVersionDatabase = 'verification-template-version/database',
  VerificationTemplateVersionDatabaseAadhaar = 'verification-template-version/database-aadhaar',
  VerificationTemplateVersionDatabaseStandard = 'verification-template-version/database-standard',
  VerificationTemplateVersionDatabaseBusiness = 'verification-template-version/database-business',
  VerificationTemplateVersionDatabaseBusinessFootprint = 'verification-template-version/database-business-footprint',
  VerificationTemplateVersionDatabaseEcbsv = 'verification-template-version/database-ecbsv',
  VerificationTemplateVersionDatabasePhoneCarrier = 'verification-template-version/database-phone-carrier',
  VerificationTemplateVersionDatabaseSerpro = 'verification-template-version/database-serpro',
  VerificationTemplateVersionDatabaseTin = 'verification-template-version/database-tin',
  VerificationTemplateVersionDatabaseVatNumber = 'verification-template-version/database-vat-number',
  VerificationTemplateVersionDigitalIdFinnishTrustNetwork = 'verification-template-version/digital-id-finnish-trust-network',
  VerificationTemplateVersionDigitalIdOneId = 'verification-template-version/digital-id-one-id',
  VerificationTemplateVersionDigitalIdSerpro = 'verification-template-version/digital-id-serpro',
  VerificationTemplateVersionDigitalIdSingpass = 'verification-template-version/digital-id-singpass',
  VerificationTemplateVersionDigitalIdUkSharecode = 'verification-template-version/digital-id-uk-sharecode',
  VerificationTemplateVersionDocument = 'verification-template-version/document',
  VerificationTemplateVersionEmailAddress = 'verification-template-version/email-address',
  VerificationTemplateVersionGovernmentId = 'verification-template-version/government-id',
  VerificationTemplateVersionGovernmentIdNfc = 'verification-template-version/government-id-nfc',
  VerificationTemplateVersionMobileDriversLicense = 'verification-template-version/mobile-drivers-license',
  VerificationTemplateVersionPhoneNumber = 'verification-template-version/phone-number',
  VerificationTemplateVersionSelfie = 'verification-template-version/selfie',

  Document = 'document',
  DocumentGeneric = 'document/generic',
  DocumentGovernmentId = 'document/government-id',
  DocumentFile = 'document-file',

  ApiKey = 'api-key',
  ApiLog = 'api-log',

  // external integrations
  ExternalIntegration = 'external-integrations',
  ExternalIntegrationCall = 'external-integration-call',
  ExternalIntegrationConfig = 'external-integration-config',
  ExternalIntegrationConfigCredentialApiKey = 'external-integration-config/http-credential/api-key-scheme',
  ExternalIntegrationConfigCredentialBearer = 'external-integration-config/http-credential/bearer-scheme',
  ExternalIntegrationConfigCredentialBasic = 'external-integration-config/http-credential/basic-scheme',
  ExternalIntegrationConfigCredentialOauth2Password = 'external-integration-config/http-credential/oauth2/password-scheme',
  ExternalIntegrationDataSource = 'external-integration/data-source',
  ExternalIntegrationDataSourceCsv = 'external-integration/data-source/csv',
  ExternalIntegrationDataSourceGoogleSheets = 'external-integration/data-source/google-sheets',
  ExternalIntegrationDataSourceMongodb = 'external-integration/data-source/mongodb',
  ExternalIntegrationDataSourceMysql = 'external-integration/data-source/mysql',
  ExternalIntegrationDataSourcePostgres = 'external-integration/data-source/postgres',
  ExternalIntegrationDataSourceRedshift = 'external-integration/data-source/redshift',
  ExternalIntegrationDataSourceS3 = 'external-integration/data-source/s3',
  ExternalIntegrationDataSourceSnowflake = 'external-integration/data-source/snowflake',
  ExternalIntegrationEnrichmentChainalysis = 'external-integration/enrichment/chainalysis',
  ExternalIntegrationEnrichmentEquifaxOneview = 'external-integration/enrichment/equifax/oneview',
  ExternalIntegrationEnrichmentKyckr = 'external-integration/enrichment/kyckr',
  ExternalIntegrationEnrichmentMiddesk = 'external-integration/enrichment/middesk',
  ExternalIntegrationEnrichmentMx = 'external-integration/enrichment/mx',
  ExternalIntegrationEnrichmentSentilink = 'external-integration/enrichment/sentilink',
  ExternalIntegrationEnrichmentTrm = 'external-integration/enrichment/trm',
  ExternalIntegrationLog = 'external-integration-log',
  ExternalIntegrationProductivity = 'external-integration/productivity',
  ExternalIntegrationProductivityHubspotOAuth = 'external-integration/productivity/hubspot-oauth',
  ExternalIntegrationProductivityOpenAi = 'external-integration/productivity/openai',
  ExternalIntegrationProductivitySalesforceOAuth = 'external-integration/productivity/salesforce-oauth',
  ExternalIntegrationProductivitySlackOAuth = 'external-integration/productivity/slack-oauth',
  ExternalIntegrationProductivityZendeskOAuth = 'external-integration/productivity/zendesk-oauth',
  ExternalIntegrationVendor = 'external-integration-vendor',

  // simulate presets
  SimulatePreset = 'simulate/preset',
  SimulatePresetVerification = 'simulate/preset/verification',

  WorkrampInstantAuth = 'workramp-instant-auth',
}

export const ExternalIntegrationDataSource = [
  ApiModel.ExternalIntegrationDataSourceCsv,
  ApiModel.ExternalIntegrationDataSourceGoogleSheets,
  ApiModel.ExternalIntegrationDataSourceMongodb,
  ApiModel.ExternalIntegrationDataSourceMysql,
  ApiModel.ExternalIntegrationDataSourcePostgres,
  ApiModel.ExternalIntegrationDataSourceRedshift,
  ApiModel.ExternalIntegrationDataSourceSnowflake,
  ApiModel.ExternalIntegrationDataSourceS3,
];

export type VerificationType =
  | ApiModel.VerificationAamva
  | ApiModel.VerificationCertificateKorea
  | ApiModel.VerificationDatabase
  | ApiModel.VerificationDatabaseBusiness
  | ApiModel.VerificationDatabaseBusinessFootprint
  | ApiModel.VerificationDatabaseEcbsv
  | ApiModel.VerificationDatabasePhoneCarrier
  | ApiModel.VerificationDatabaseSerpro
  | ApiModel.VerificationDatabaseStandard
  | ApiModel.VerificationDatabaseTin
  | ApiModel.VerificationDigitalIdUkSharecode
  | ApiModel.VerificationDocument
  | ApiModel.VerificationEmailAddress
  | ApiModel.VerificationGovernmentId
  | ApiModel.VerificationGovernmentIdNfc
  | ApiModel.VerificationMobileDriversLicense
  | ApiModel.VerificationPhoneNumber
  | ApiModel.VerificationSelfie;

export type PresetType = ApiModel.SimulatePresetVerification;

export type ApiResponseMeta<M = {}> = M & {
  [key: string]: string & string[] & string[][];
} & {
  'feature-flags'?: FeatureFlagsMap;
  'product-features'?: ProductFeaturesMap;
};

export interface ApiResponse<T extends ApiResource, M = {}> {
  data: T;
  included?: ApiResource[];
  meta?: ApiResponseMeta<M>;
}

export interface ApiListResponse<T extends ApiResource, M = {}> {
  data: T[];
  included?: ApiResource[];
  meta?: ApiResponseMeta<M>;
}

/**
 * Base interface for API resources. Should never be directly used.
 */
export interface BaseApiResource<A, R, Type> {
  type: Type;
  id: string;
  attributes: A;
  relationships: R;
}

/**
 * A generic API data supertype.
 *
 * Without any type parameters, ApiResource can be used for functions that take
 * generic resources without using `attributes` or `relationships`.
 *
 * To narrow down acceptable attributes to those of a specific resource, a new
 * type can be declared that passes type parameters. If passed, the `Type`
 * param can acts as the tag for a discriminated union. This can be used to
 * define sum types of related resources, e.g.:
 *
 * ```typescript
 * type ResourceFoo = ApiResource<{ foo: string }, {}, ApiModel.Foo>;
 * type ResourceBar = ApiResource<{ bar: string }, {}, ApiModel.Bar>;
 * type RelatedResources = ResourceFoo | ResourceBar;
 * ```
 *
 * Then, functions that operate on `RelatedResources` can refine the resource
 * type by inspecting `type`, e.g.:
 *
 * ```typescript
 * function (resource: RelatedResources) {
 *   // If we accessed resource.foo or resource.bar we'd get a type error
 *   switch (resource.type) {
 *     case ApiModel.Foo:
 *       // resource's type is now ResourceFoo and we can access resource.foo
 *     case ApiModel.Bar:
 *       // resource's type is now ResourceBar and we can access resource.bar
 *   }
 * }
 * ```
 */
export type ApiResource<A = {}, R = {}, Type extends ApiModel = ApiModel> = Omit<
  BaseApiResource<A, R, Type>,
  (keyof A extends never ? 'attributes' : never) | (keyof R extends never ? 'relationships' : never)
>;

export interface ApiRelationship<Type extends ApiModel = ApiModel> {
  data: ApiRelationshipIdentifier<Type>;
}

export type ApiOptionalRelationship<Type extends ApiModel = ApiModel> =
  | ApiRelationship<Type>
  | { data: null };

export interface ApiListRelationship<Type extends ApiModel = ApiModel> {
  data: ApiRelationshipIdentifier<Type>[];
}

export interface ApiRelationshipIdentifier<Type extends ApiModel = ApiModel> {
  type: Type;
  id: string;
  attributes?: Record<string, any>;
}

export interface PaginationParams {
  'current-page': number;
  'total-count': number;
  'total-pages': number;
  'lower-bound'?: boolean;
}

/**
 * Type used to represent API payloads that contain attributes that do not
 * exist on the associated type for endpoints.
 * For example, the password update route takes the attribute
 * `'password-confirmation'` which does not exist on the User type.
 */
export type ApiPayload<Attributes extends {}, Type extends ApiModel = ApiModel> = ApiResource<
  Attributes,
  {},
  Type
>;

/**
 * Type used to represent API payloads that contain attributes that do not
 * exist on the associated type for endpoints that don't take ID (singleton
 * or create).
 * For example, the password update route takes the attribute
 * `'password-confirmation'` which does not exist on the User type.
 */
export type ApiSingletonPayload<Attributes extends {}, Type extends ApiModel = ApiModel> = Omit<
  ApiResource<Attributes, {}, Type>,
  'id'
>;
